// -----------------------------------------------------------------------------
// This file contains the modes of the UI
// -----------------------------------------------------------------------------

// ----------- IMPORTS ------------------------------------------------------------

// React
import { useMemo } from 'react';

// Zustand store
import { getGrid, useLoadingStore } from '../../store/LoadingStore';
import { useLayersBucket } from './persistent/persistentStore';
import { LoadingStatus } from '../../store/LoadingStore';

// Geodata
import { SimulationSet } from '../../data/SimulationSet';

// ----------- ENUMS ------------------------------------------------------------

export enum UIMode {
    SiteAnalysis = 'siteAnalysis',
    EditGeodata = 'editGeodata',
}

export enum ModeReason {
    None = 'none',
    NoSimSet = 'no-simset',
    NoLayer = 'no-layer',
    EmptyLayer = 'empty-layer',
}

// ----------- TYPES ------------------------------------------------------------
type Status = {
    hasSimSet: boolean;
    hasAnyLayer: boolean;
    hasEmptyLayer: boolean;
};

// ----------- HELPERS ------------------------------------------------------------

// Introspect the grid and compute the minimal status needed for mode decision
export function computeStatusFromGrid(engineReady: boolean): Status {
    if (!engineReady) return { hasSimSet: false, hasAnyLayer: false, hasEmptyLayer: false };

    const grid = getGrid?.();
    const geodata = grid?.getActiveGeodata?.();

    // No active geodata, treat as empty.
    if (!geodata) {
        return { hasSimSet: false, hasAnyLayer: false, hasEmptyLayer: false };
    }

    //simulationSets existence check
    const simSets = geodata.simulationSets ?? [];

    const hasSimSet = simSets.length > 0;

    // // Current Simulation Set
    // let currentSet = grid.getSimulationSet();

    // Current Simulation Set
    let currentSet: SimulationSet | undefined;

    try {
        const activeLayer = grid?.getActiveLayer?.();

        currentSet = activeLayer?.parentSimulationSet ?? grid?.getSimulationSet?.() ?? undefined;
    } catch {
        // No active layer / invalid UID at startup, stay in Edit until selection exists.
        currentSet = undefined;
    }

    if (!currentSet) {
        return { hasSimSet, hasAnyLayer: false, hasEmptyLayer: false };
    }

    // CHeck layers
    const layers = currentSet.layerCollection ?? [];
    const hasAnyLayer = layers.length > 0;
    const hasEmptyLayer = currentSet.hasEmptyLayer === true;

    return { hasSimSet, hasAnyLayer, hasEmptyLayer };
}

// ----------- MODE DECISION --------------------------------------------
function decideMode(status: Status): { mode: UIMode; reason: ModeReason } {
    if (!status.hasSimSet) return { mode: UIMode.EditGeodata, reason: ModeReason.NoSimSet };
    if (!status.hasAnyLayer) return { mode: UIMode.EditGeodata, reason: ModeReason.NoLayer };
    if (status.hasEmptyLayer) return { mode: UIMode.EditGeodata, reason: ModeReason.EmptyLayer };
    return { mode: UIMode.SiteAnalysis, reason: ModeReason.None };
}

export function useUIMode(): { mode: UIMode; reason: ModeReason } {
    const loadingStatus = useLoadingStore((s) => s.loadingStatus);
    const engineReady = loadingStatus === LoadingStatus.VISUALIZER_READY;

    const activeLayerID = useLayersBucket((s) => s.activeLayerID);

    return useMemo(() => decideMode(computeStatusFromGrid(engineReady)), [engineReady, activeLayerID]);
}
